import type { AuditLogDto, Paginated } from '@cartly/shared';
import { Alert, Badge, Button, Card, EmptyState, Spinner, type Tone } from '@cartly/ui';
import { ScrollText } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../../api/client';
import { formatDateTime } from '../../app/format';
import { useLoader } from '../../app/hooks';
import { useActiveSession } from '../../app/session';

const ACTION_LABELS: Record<string, string> = {
  'tenant.created': 'Organization created',
  'tenant.updated': 'Organization updated',
  'member.invited': 'Teammate invited',
  'invitation.revoked': 'Invitation revoked',
  'invitation.accepted': 'Invitation accepted',
  'member.role_changed': 'Role changed',
  'member.status_changed': 'Member status changed',
  'member.removed': 'Member removed',
  'member.left': 'Member left',
  'role.created': 'Role created',
  'role.updated': 'Role updated',
  'role.deleted': 'Role deleted',
};

function toneFor(action: string): Tone {
  if (action.endsWith('.removed') || action.endsWith('.deleted') || action.endsWith('.revoked'))
    return 'critical';
  if (action.endsWith('.created') || action.endsWith('.accepted')) return 'success';
  return 'neutral';
}

export function AuditLogSettings() {
  const session = useActiveSession();
  const [more, setMore] = useState<AuditLogDto[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);

  const { data, error, loading } = useLoader(async () => {
    const page = await api.get<Paginated<AuditLogDto>>('/audit-logs?limit=50');
    setMore([]);
    setCursor(page.nextCursor);
    return page.items;
  }, session.activeTenant?.id);

  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const page = await api.get<Paginated<AuditLogDto>>(`/audit-logs?limit=50&cursor=${cursor}`);
      setMore((m) => [...m, ...page.items]);
      setCursor(page.nextCursor);
    } catch (err) {
      setMoreError(errorMessage(err));
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading && !data) return <Spinner size="lg" label="Loading" />;
  if (error || !data) return <Alert tone="critical">{error ?? 'Failed to load'}</Alert>;
  const rows = [...data, ...more];

  return (
    <Card
      title="Audit log"
      subtitle="Security- and data-relevant activity in this organization."
      flush
    >
      {rows.length === 0 ? (
        <EmptyState icon={<ScrollText size={22} />} title="No activity yet" />
      ) : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Actor</th>
                  <th>IP address</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <Badge tone={toneFor(r.action)}>{ACTION_LABELS[r.action] ?? r.action}</Badge>
                    </td>
                    <td>
                      {r.actor ? (
                        <>
                          <div className="table__primary">{r.actor.name}</div>
                          <div className="table__secondary">{r.actor.email}</div>
                        </>
                      ) : (
                        <span className="text-muted">{r.actorType}</span>
                      )}
                    </td>
                    <td className="mono text-sm">{r.ipAddress ?? '—'}</td>
                    <td className="text-muted">{formatDateTime(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(cursor || moreError) && (
            <div className="card__footer">
              {moreError && <span className="field__error">{moreError}</span>}
              {cursor && (
                <Button size="sm" loading={loadingMore} onClick={() => void loadMore()}>
                  Load more
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
}
